import React, { ReactNode, useState } from 'react';
import { AlertTriangleIcon, CalendarClockIcon, CheckCircle2Icon, LinkIcon, LockIcon, PauseCircleIcon, PlayCircleIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { MaternityFile } from '../../types';
import { applyScheduleOverride, formatReviewDate, getPostnatalReviewStatus, PostnatalReviewKind, PostnatalReviewStatus } from './postnatalSchedule';
type PostnatalReviewSectionProps = {
  file: MaternityFile;
  updatedBy: string;
  onChange: (file: MaternityFile) => void;
  onOpenDischargeSlip: () => void;
  sixDayForm: ReactNode;
  sixWeekForm: ReactNode;
};
export function PostnatalReviewSection({
  file,
  updatedBy,
  onChange,
  onOpenDischargeSlip,
  sixDayForm,
  sixWeekForm
}: PostnatalReviewSectionProps) {
  const sixDays = getPostnatalReviewStatus(file, 'sixDays');
  const sixWeeks = getPostnatalReviewStatus(file, 'sixWeeks');
  const update = (kind: PostnatalReviewKind, patch: Parameters<typeof applyScheduleOverride>[2]) => onChange(applyScheduleOverride(file, kind, patch, updatedBy));
  return <section className="space-y-5" aria-labelledby="postnatal-review-heading" data-id="element-4135" data-mp="1an02k5do6dsg">
      <div className="rounded-lg border border-pink-200 bg-white shadow-sm" data-id="element-4136" data-mp="15up2n2ixirzw">
        <div className="border-b border-pink-100 bg-pink-50/60 px-5 py-4" data-id="element-4137" data-mp="10w5h3388zi82">
          <h2 id="postnatal-review-heading" className="text-lg font-bold text-gray-900" data-id="element-4138" data-mp="10vvrhgr47d2w">
            Postnatal Review
          </h2>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-gray-600" data-id="element-4139" data-mp="qwwsyr02cxtm">
            <LinkIcon className="h-3.5 w-3.5 text-pink-600" data-id="element-4140" data-mp="mvur02eri7ny" />
            Linked to maternity record {file.bmcFileNumber} ·{' '}
            {file.patient?.firstName} {file.patient?.surname}
          </p>
        </div>
        <div className="grid gap-4 px-5 py-4 sm:grid-cols-2" data-id="element-4141" data-mp="2h9cfpsqwlakk">
          <ScheduleCard status={sixDays} onUpdate={update} data-id="element-4142" data-mp="2cbkbq0czhy00" />
          <ScheduleCard status={sixWeeks} onUpdate={update} data-id="element-4143" data-mp="2cc2g5k9svij0" />
        </div>
        {(!sixDays.isScheduled || !sixWeeks.isScheduled) && <p className="border-t border-pink-100 px-5 py-3 text-sm text-gray-600" data-id="element-4144" data-mp="306dziw3cutt5">
            Review dates are scheduled from the Discharge Slip.{' '}
            <button type="button" onClick={onOpenDischargeSlip} className="font-semibold text-pink-700 underline hover:text-pink-800 focus:outline-none focus:ring-2 focus:ring-pink-500" data-id="element-4145" data-mp="2mgsoq8zplkw7">
              Open Discharge Slip
            </button>{' '}
            to enter the missing date.
          </p>}
      </div>

      <ReviewPanel kind="sixDays" status={sixDays} onOpenDischargeSlip={onOpenDischargeSlip} onUpdate={update} data-id="element-4146" data-mp="354nvh8wwlyft">
        {sixDayForm}
      </ReviewPanel>

      <ReviewPanel kind="sixWeeks" status={sixWeeks} onOpenDischargeSlip={onOpenDischargeSlip} onUpdate={update} data-id="element-4147" data-mp="1tqak33jc7bgo">
        {sixWeekForm}
      </ReviewPanel>
    </section>;
}
type UpdateFn = (kind: PostnatalReviewKind, patch: {
  rescheduledDate?: string;
  suspended?: boolean;
  reason?: string;
}) => void;
function ScheduleCard({
  status,
  onUpdate
}: {
  status: PostnatalReviewStatus;
  onUpdate: UpdateFn;
}) {
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState(status.scheduledDate);
  const [reason, setReason] = useState(status.reason);
  const save = () => {
    onUpdate(status.kind, {
      rescheduledDate: date,
      reason,
      suspended: false
    });
    setEditing(false);
  };
  return <div className="rounded-lg border border-gray-200 p-4" data-id="element-4148" data-mp="1h87x7hvjnlrg">
      <div className="flex items-start justify-between gap-3" data-id="element-4149" data-mp="1h9suhf98tit0">
        <div data-id="element-4150" data-mp="1cgg7tao4jm2w">
          <p className="text-sm font-semibold text-gray-900" data-id="element-4151" data-mp="1cfwzmloa0c22">
            Postnatal Review – {status.label}
          </p>
          <p className="mt-1 text-sm text-gray-600" data-id="element-4152" data-mp="opat4ytx6zu6">
            Scheduled: {formatReviewDate(status.scheduledDate)}
          </p>
          {status.isRescheduled && <p className="mt-0.5 text-xs text-gray-500" data-id="element-4153" data-mp="xguddbgad8rd">
              Rescheduled from {formatReviewDate(status.originalDate)}
              {status.updatedBy ? ` by ${status.updatedBy}` : ''}
            </p>}
          {status.reason && <p className="mt-0.5 text-xs italic text-gray-500" data-id="element-4154" data-mp="1c4p9z2xezx9q">
              Reason: {status.reason}
            </p>}
        </div>
        <StatusBadge status={status} data-id="element-4155" data-mp="1g593lwis4ir4" />
      </div>

      {editing ? <div className="mt-3 space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3" data-id="element-4156" data-mp="1l42i9s9syfsv">
          <div data-id="element-4157" data-mp="1q3oxu8d3s84u">
            <label htmlFor={`reschedule-date-${status.kind}`} className="mb-1 block text-xs font-medium text-gray-700" data-id="element-4158" data-mp="1q4zyw0f5z1s9">
              New review date
            </label>
            <input id={`reschedule-date-${status.kind}`} type="date" value={date} onChange={event => setDate(event.target.value)} className="w-full rounded border border-gray-300 p-2 text-sm focus:border-pink-500 focus:outline-none focus:ring-1 focus:ring-pink-500" data-id="element-4159" data-mp="3rwwbzegzcjp5" />
          </div>
          <div data-id="element-4160" data-mp="256gj909uhdl2">
            <label htmlFor={`reschedule-reason-${status.kind}`} className="mb-1 block text-xs font-medium text-gray-700" data-id="element-4161" data-mp="2581gixnjnamm">
              Reason
            </label>
            <input id={`reschedule-reason-${status.kind}`} type="text" value={reason} onChange={event => setReason(event.target.value)} placeholder="e.g. Mother travelling, clinic rescheduled" className="w-full rounded border border-gray-300 p-2 text-sm focus:border-pink-500 focus:outline-none focus:ring-1 focus:ring-pink-500" data-id="element-4162" data-mp="2wwuvks27mhum" />
          </div>
          <div className="flex gap-2" data-id="element-4163" data-mp="1cfyc9o602s2d">
            <Button size="sm" onClick={save} disabled={!date} className="bg-pink-600 hover:bg-pink-700 focus:ring-pink-500" data-id="element-4164" data-mp="17iycfuzquljp">
              Save new date
            </Button>
            <Button size="sm" variant="outline" onClick={() => {
          setDate(status.scheduledDate);
          setReason(status.reason);
          setEditing(false);
        }} data-id="element-4165" data-mp="1dxdq9g8s262v">
              Cancel
            </Button>
          </div>
        </div> : <div className="mt-3 flex flex-wrap gap-2" data-id="element-4166" data-mp="v15ju9413p5z">
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-id="element-4167" data-mp="v1x5os7tq8j2">
            <CalendarClockIcon className="mr-1.5 h-4 w-4" data-id="element-4168" data-mp="hasaq3fei4f2" /> Reschedule
          </Button>
          {status.isSuspended ? <Button size="sm" variant="outline" onClick={() => onUpdate(status.kind, {
        suspended: false
      })} data-id="element-4169" data-mp="l8q2ne0k34m5">
              <PlayCircleIcon className="mr-1.5 h-4 w-4" data-id="element-4170" data-mp="3otgu3l55fxyi" /> Resume review
            </Button> : <Button size="sm" variant="outline" disabled={status.isCompleted} onClick={() => {
        const entered = window.prompt(`Reason for suspending the ${status.label.toLowerCase()} postnatal review:`, status.reason);
        if (entered === null) return;
        onUpdate(status.kind, {
          suspended: true,
          reason: entered
        });
      }} data-id="element-4171" data-mp="3jv5k9aeutnga">
              <PauseCircleIcon className="mr-1.5 h-4 w-4" data-id="element-4172" data-mp="lc8ez91t3c7h" /> Suspend
            </Button>}
        </div>}
    </div>;
}
function StatusBadge({
  status
}: {
  status: PostnatalReviewStatus;
}) {
  if (status.isCompleted) return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800" data-id="element-4173" data-mp="qcmj4g0ae208">
        <CheckCircle2Icon className="h-3.5 w-3.5" data-id="element-4174" data-mp="1wqiyi9na590f" /> Documented
      </span>;
  if (status.isSuspended) return <span className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-800" data-id="element-4175" data-mp="21s8apsm8nsq3">
        <PauseCircleIcon className="h-3.5 w-3.5" data-id="element-4176" data-mp="2aqjy7lrt6aeb" /> Suspended
      </span>;
  if (status.isOverdue) return <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-800" data-id="element-4177" data-mp="1e1cd2ev3q3ax">
        <AlertTriangleIcon className="h-3.5 w-3.5" data-id="element-4178" data-mp="1j05qy8eavwbm" /> Overdue
      </span>;
  if (status.isDue) return <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800" data-id="element-4179" data-mp="1mzy4z0mgquod">
        <CalendarClockIcon className="h-3.5 w-3.5" data-id="element-4180" data-mp="va1nq250xyl5" /> Due today
      </span>;
  if (status.isScheduled) return <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-800" data-id="element-4181" data-mp="zb56oxidcaql">
        In {status.daysUntilDue} days
      </span>;
  return <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700" data-id="element-4182" data-mp="msj9ohb630xw">
      Not scheduled
    </span>;
}
function ReviewPanel({
  kind,
  status,
  onOpenDischargeSlip,
  onUpdate,
  children
}: {
  kind: PostnatalReviewKind;
  status: PostnatalReviewStatus;
  onOpenDischargeSlip: () => void;
  onUpdate: UpdateFn;
  children: ReactNode;
}) {
  const headingId = `postnatal-${kind}-heading`;
  return <section className="space-y-3" aria-labelledby={headingId} data-id="element-4183" data-mp="40goqtrhsyhu">
      <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-4184" data-mp="3v9nserjapc3y">
        <h3 id={headingId} className="text-base font-bold text-gray-900" data-id="element-4185" data-mp="3r7sqbktjiup4">
          Postnatal Review – {status.label}
        </h3>
        <StatusBadge status={status} data-id="element-4186" data-mp="3m6v2osprh8g8" />
      </div>

      {status.isSuspended ? <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center" data-id="element-4187" data-mp="3ha4jvxmknyr4">
          <PauseCircleIcon className="mx-auto h-6 w-6 text-gray-400" data-id="element-4188" data-mp="35d30p918t28c" />
          <p className="mt-2 text-sm font-medium text-gray-900" data-id="element-4189" data-mp="31csrzom2fxps">
            Review suspended
          </p>
          <p className="mt-1 text-sm text-gray-600" data-id="element-4190" data-mp="3f4h4vu452q7t">
            {status.reason ? `Reason: ${status.reason}` : 'No reason recorded.'}{' '}
            Notifications are paused until it is resumed.
          </p>
          <Button size="sm" variant="outline" className="mt-3" onClick={() => onUpdate(kind, {
        suspended: false
      })} data-id="element-4191" data-mp="2mfsb6g5exn63">
            <PlayCircleIcon className="mr-1.5 h-4 w-4" data-id="element-4192" data-mp="2red1nnuv4shc" /> Resume review
          </Button>
        </div> : status.isActive ? <>
          {status.isDue && <p role="status" className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900" data-id="element-4193" data-mp="ble76aczyofv">
              Mother and baby are due for their {status.label.toLowerCase()}{' '}
              postnatal review
              {status.isOverdue ? ` (${Math.abs(status.daysUntilDue || 0)} day(s) overdue).` : ' today.'}{' '}
              Document both reviews below.
            </p>}
          {children}
        </> : <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center" data-id="element-4194" data-mp="2898ai7jdjp4c">
          <LockIcon className="mx-auto h-6 w-6 text-gray-400" data-id="element-4195" data-mp="2cbv4adzlvk46" />
          <p className="mt-2 text-sm font-medium text-gray-900" data-id="element-4196" data-mp="2cd69mi8p8zxe">
            {status.isScheduled ? `Opens on ${formatReviewDate(status.scheduledDate)}` : 'No review date scheduled'}
          </p>
          <p className="mt-1 text-sm text-gray-600" data-id="element-4197" data-mp="2r8vxyn1cxp5a">
            {status.isScheduled ? 'The mother and baby forms activate automatically on the scheduled date.' : 'Enter the postnatal review date on the Discharge Slip to schedule this review.'}
          </p>
          {!status.isScheduled && <button type="button" onClick={onOpenDischargeSlip} className="mt-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-pink-500" data-id="element-4198" data-mp="30x9mgatkvlrv">
              Open Discharge Slip
            </button>}
        </div>}
    </section>;
}